import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import { Aviso, Boton, Campo, EncabezadoPagina, FilaLista, Insignia, Selector, Tarjeta } from '../../componentes/ui.tsx';
import { LayoutTemplate } from 'lucide-react';

interface PlantillaResumen {
  id: string;
  nombre: string;
  tipoContenido: string;
  numProductos: number;
  estado: 'BORRADOR' | 'PUBLICADA' | 'ARCHIVADA';
  formatos: string[];
}

export function PaginaAdminPlantillas() {
  const qc = useQueryClient();
  const [nombre, setNombre] = useState('');
  const [tipoContenido, setTipoContenido] = useState('PROMOCION');
  const [numProductos, setNumProductos] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const plantillas = useQuery({
    queryKey: ['admin-plantillas'],
    queryFn: () => api<{ items: PlantillaResumen[] }>('/admin/plantillas'),
  });

  const crear = useMutation({
    mutationFn: () =>
      api<{ id: string }>('/admin/plantillas', {
        metodo: 'POST',
        cuerpo: { nombre: nombre.trim(), tipoContenido, numProductos },
      }),
    onSuccess: async () => {
      setNombre('');
      await qc.invalidateQueries({ queryKey: ['admin-plantillas'] });
    },
    onError: (e) => setError(e.message),
  });

  const duplicar = useMutation({
    mutationFn: (id: string) => api(`/admin/plantillas/${id}/duplicar`, { metodo: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-plantillas'] }),
  });

  const archivar = useMutation({
    mutationFn: (id: string) =>
      api(`/admin/plantillas/${id}`, { metodo: 'PATCH', cuerpo: { estado: 'ARCHIVADA' } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-plantillas'] }),
  });

  return (
    <main className="mx-auto w-full max-w-[1600px] space-y-4">
      <EncabezadoPagina titulo="Plantillas" subtitulo="Se diseñan una por una y se publican (D12, D26)" />

      <form
        className="tarjeta grid items-end gap-3 p-5 sm:grid-cols-[2fr_1fr_1fr_auto]"
        onSubmit={(e) => {
          e.preventDefault();
          crear.mutate();
        }}
      >
        <Campo etiqueta="Nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} required />
        <Selector etiqueta="Tipo" value={tipoContenido} onChange={(e) => setTipoContenido(e.target.value)}>
          <option value="PROMOCION">Promoción</option>
          <option value="NUEVO_INGRESO">Nuevo ingreso</option>
          <option value="PERSONALIZAR_IMAGEN">Personalizar imagen</option>
        </Selector>
        <Selector
          etiqueta="Productos"
          value={String(numProductos)}
          onChange={(e) => setNumProductos(Number(e.target.value))}
        >
          <option value="1">1</option>
          <option value="2">2</option>
          <option value="3">3</option>
        </Selector>
        <div className="flex items-end">
          <Boton type="submit" disabled={crear.isPending}>Crear</Boton>
        </div>
      </form>
      {error ? <Aviso>{error}</Aviso> : null}

      <Tarjeta>
        <ul className="divide-y divide-superficie2">
          {plantillas.data?.items.map((p) => (
            <li key={p.id}>
              <FilaLista
                avatar={<span className="avatar"><LayoutTemplate size={18} /></span>}
                titulo={
                  <>
                    {p.nombre}
                    <span className="ml-2">
                      <Insignia tono={p.estado === 'PUBLICADA' ? 'acento' : p.estado === 'BORRADOR' ? 'gris' : 'gris'}>
                        {p.estado}
                      </Insignia>
                    </span>
                  </>
                }
                subtitulo={`${p.tipoContenido} · ${p.numProductos} producto(s) · formatos: ${p.formatos.join(', ') || '—'}`}
                acciones={
                  <>
                    <Link
                      to="/admin/plantillas/$plantillaId"
                      params={{ plantillaId: p.id }}
                      className="inline-flex min-h-9 items-center rounded-full px-4 text-[13px] font-medium"
                      style={{ backgroundColor: 'var(--boton-bg)', color: 'var(--boton-texto)' }}
                    >
                      Diseñar
                    </Link>
                    <Boton variante="contorno" tamano="sm" onClick={() => duplicar.mutate(p.id)}>Duplicar</Boton>
                    {p.estado !== 'ARCHIVADA' ? (
                      <Boton variante="texto" tamano="sm" onClick={() => archivar.mutate(p.id)}>Archivar</Boton>
                    ) : null}
                  </>
                }
              />
            </li>
          ))}
        </ul>
      </Tarjeta>
    </main>
  );
}
